import { useMemo, useState } from "react";
import SiteHeader from "@/components/feature/SiteHeader";
import SiteFooter from "@/components/feature/SiteFooter";
import PageHeader from "@/components/feature/PageHeader";
import WorkCard from "@/components/feature/WorkCard";
import Pagination from "@/components/base/Pagination";
import EmptyState from "@/components/base/EmptyState";
import Reveal from "@/components/base/Reveal";
import { works as worksData } from "@/mocks/works";
import type { Work } from "@/types/content";

const works = worksData as Work[];

const types = ["全部", "电影", "电视剧", "短片"];
const yearRanges = [
  { label: "全部年份", min: 0, max: 9999 },
  { label: "2018 - 2020", min: 2018, max: 2020 },
  { label: "2021 - 2022", min: 2021, max: 2022 },
  { label: "2023 - 2024", min: 2023, max: 2024 },
];
const sorts = [
  { key: "newest", label: "上映时间（新→旧）" },
  { key: "oldest", label: "上映时间（旧→新）" },
  { key: "title", label: "名称排序" },
];

const PER_PAGE = 8;

export default function Works() {
  const [type, setType] = useState("全部");
  const [yearIndex, setYearIndex] = useState(0);
  const [sort, setSort] = useState("newest");
  const [page, setPage] = useState(1);
  const [drawerOpen, setDrawerOpen] = useState(false);

  const filtered = useMemo(() => {
    const range = yearRanges[yearIndex];
    const list = works.filter((w) => {
      const typeMatch = type === "全部" || w.type === type;
      const yearMatch = w.year >= range.min && w.year <= range.max;
      return typeMatch && yearMatch;
    });
    return [...list].sort((a, b) => {
      if (sort === "oldest") return a.year - b.year;
      if (sort === "title") return a.title.localeCompare(b.title, "zh-Hans-CN");
      return b.year - a.year;
    });
  }, [type, yearIndex, sort]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PER_PAGE));
  const safePage = Math.min(page, totalPages);
  const paged = filtered.slice((safePage - 1) * PER_PAGE, safePage * PER_PAGE);

  const resetFilters = () => {
    setType("全部");
    setYearIndex(0);
    setSort("newest");
    setPage(1);
  };

  const filterActive = type !== "全部" || yearIndex !== 0 || sort !== "newest";

  const FilterGroups = (
    <div className="space-y-7">
      <div>
        <p className="text-xs font-semibold tracking-wide text-foreground-500 mb-3">作品类型</p>
        <div className="inline-flex flex-wrap gap-1 p-1 rounded-full bg-background-100 border border-background-200">
          {types.map((t) => (
            <button
              key={t}
              type="button"
              onClick={() => {
                setType(t);
                setPage(1);
              }}
              className={[
                "px-4 py-1.5 rounded-full text-sm transition-colors cursor-pointer whitespace-nowrap",
                type === t
                  ? "bg-primary-500 text-background-50"
                  : "text-foreground-700 hover:text-primary-700",
              ].join(" ")}
            >
              {t}
            </button>
          ))}
        </div>
      </div>

      <div>
        <p className="text-xs font-semibold tracking-wide text-foreground-500 mb-3">年份区间</p>
        <div className="inline-flex flex-wrap gap-1 p-1 rounded-full bg-background-100 border border-background-200">
          {yearRanges.map((r, idx) => (
            <button
              key={r.label}
              type="button"
              onClick={() => {
                setYearIndex(idx);
                setPage(1);
              }}
              className={[
                "px-4 py-1.5 rounded-full text-sm transition-colors cursor-pointer whitespace-nowrap",
                yearIndex === idx
                  ? "bg-secondary-500 text-background-50"
                  : "text-foreground-700 hover:text-primary-700",
              ].join(" ")}
            >
              {r.label}
            </button>
          ))}
        </div>
      </div>

      <div>
        <p className="text-xs font-semibold tracking-wide text-foreground-500 mb-3">作品排序</p>
        <div className="relative max-w-xs">
          <select
            value={sort}
            onChange={(e) => {
              setSort(e.target.value);
              setPage(1);
            }}
            className="w-full appearance-none px-4 py-2.5 pr-10 rounded-md border border-background-300 bg-background-50 text-sm text-foreground-800 focus:outline-none focus:ring-2 focus:ring-primary-400 cursor-pointer"
          >
            {sorts.map((s) => (
              <option key={s.key} value={s.key}>
                {s.label}
              </option>
            ))}
          </select>
          <i className="ri-arrow-down-s-line absolute right-3 top-1/2 -translate-y-1/2 text-foreground-500 pointer-events-none"></i>
        </div>
      </div>
    </div>
  );

  return (
    <div className="w-full min-h-screen bg-background-50">
      <title>奥田咲影视作品 - 类型及年份筛选</title>
      <meta
        name="description"
        content="奥田咲影视作品聚合页，支持按电影、电视剧、短片类型及年份区间筛选，快速查阅奥田咲全部影视作品档案与简介。"
      />
      <meta name="keywords" content="奥田咲作品,奥田咲影视作品,奥田咲电影,奥田咲电视剧" />

      <SiteHeader />
      <PageHeader
        eyebrow="FILMOGRAPHY"
        title="奥田咲影视作品"
        description="汇总奥田咲自出道以来的电影、电视剧与短片作品。可按作品类型与年份区间筛选，查看每部作品的演职信息与简介。"
        crumbs={[{ label: "首页", to: "/" }, { label: "影视作品" }]}
        extra={
          <div className="flex items-center gap-6">
            <div className="text-center">
              <p className="font-heading text-3xl font-bold text-accent-400">{works.length}</p>
              <p className="mt-1 text-xs text-background-400">收录作品</p>
            </div>
            <div className="w-px h-10 bg-primary-700"></div>
            <div className="text-center">
              <p className="font-heading text-3xl font-bold text-secondary-300">
                {works.filter((w) => w.year >= 2023).length}
              </p>
              <p className="mt-1 text-xs text-background-400">近两年新作</p>
            </div>
          </div>
        }
      />

      <main className="w-full px-4 md:px-8 py-10 md:py-14">
        <div className="w-full max-w-7xl mx-auto">
          <div className="hidden lg:block p-6 rounded-lg bg-background-50 border border-background-200 mb-10">
            {FilterGroups}
          </div>

          <div className="lg:hidden flex items-center justify-between gap-3 mb-6">
            <p className="text-sm text-foreground-600">
              共 <span className="font-semibold text-foreground-900">{filtered.length}</span> 部作品
            </p>
            <button
              type="button"
              onClick={() => setDrawerOpen(true)}
              className="inline-flex items-center gap-1.5 px-4 py-2.5 rounded-md border border-background-300 text-sm font-medium text-foreground-800 hover:border-primary-400 hover:text-primary-700 transition-colors cursor-pointer whitespace-nowrap"
            >
              <i className="ri-filter-3-line"></i>
              筛选
              {filterActive && <span className="w-1.5 h-1.5 rounded-full bg-accent-500"></span>}
            </button>
          </div>

          {paged.length > 0 ? (
            <>
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-5">
                {paged.map((work, idx) => (
                  <Reveal key={work.id} delay={idx * 40}>
                    <WorkCard work={work} />
                  </Reveal>
                ))}
              </div>
              <Pagination page={safePage} totalPages={totalPages} onChange={setPage} />
            </>
          ) : (
            <EmptyState
              icon="ri-film-line"
              title="没有找到符合条件的作品"
              description="当前筛选条件下暂时没有匹配的影视作品，试试调整作品类型或年份区间。"
              actionLabel="重置筛选条件"
              onAction={resetFilters}
            />
          )}
        </div>
      </main>

      <SiteFooter />

      {drawerOpen && (
        <div className="lg:hidden fixed inset-0 z-[60]">
          <div
            className="absolute inset-0 bg-black/50"
            onClick={() => setDrawerOpen(false)}
            aria-hidden="true"
          ></div>
          <div className="absolute top-0 right-0 h-full w-[85%] max-w-sm bg-background-50 p-5 overflow-y-auto animate-fade-in">
            <div className="flex items-center justify-between mb-6">
              <h3 className="font-heading text-lg font-bold text-foreground-950">筛选条件</h3>
              <button
                type="button"
                onClick={() => setDrawerOpen(false)}
                aria-label="关闭"
                className="w-9 h-9 flex items-center justify-center rounded-md text-foreground-700 hover:bg-background-100 cursor-pointer"
              >
                <i className="ri-close-line text-xl"></i>
              </button>
            </div>
            {FilterGroups}
            <div className="mt-8 flex flex-col gap-3">
              <button
                type="button"
                onClick={() => setDrawerOpen(false)}
                className="w-full px-4 py-3 rounded-md bg-primary-500 text-background-50 text-sm font-semibold hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
              >
                查看 {filtered.length} 部作品
              </button>
              <button
                type="button"
                onClick={resetFilters}
                className="w-full px-4 py-3 rounded-md border border-background-300 text-sm font-medium text-foreground-800 hover:border-primary-400 transition-colors cursor-pointer whitespace-nowrap"
              >
                重置筛选
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}